import { createSelector } from '@reduxjs/toolkit';

import { useAppSelector } from './hooks/use-app-selector';

type Selector<TSelectValue, TArgs extends unknown[]> = (
    state: AppStateSchema,
    ...args: TArgs
) => TSelectValue;
type Hook<TSelectValue, TArgs extends unknown[]> = (
    ...args: TArgs
) => TSelectValue;
type Result<TSelectValue, TArgs extends unknown[]> = [
    Hook<TSelectValue, TArgs>,
    Selector<TSelectValue, TArgs>,
];

export function buildSelector<TSelectValue, TArgs extends unknown[]>(
    selector: Selector<TSelectValue, TArgs>,
): Result<TSelectValue, TArgs> {
    // Create a memoized version of the selector for each unique set of arguments
    const memoizedSelector = (state: AppStateSchema, ...args: TArgs) => {
        // We create a new selector for each unique set of args
        // This ensures proper memoization even with different args
        const selectWithArgs = createSelector(
            (s: AppStateSchema) => s,
            (s) => selector(s, ...args),
        );

        return selectWithArgs(state);
    };

    // Hook that uses the memoized selector
    const useSelectorHook: Hook<TSelectValue, TArgs> = (...args: TArgs) =>
        useAppSelector((state) => memoizedSelector(state, ...args));

    return [useSelectorHook, memoizedSelector as Selector<TSelectValue, TArgs>];
}
